import { Link, useRouterState } from '@tanstack/react-router';
import { CircleHelp, LogOut, type LucideIcon, Search, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavScroll } from './NavScroll.tsx';
import { logout } from '../auth.tsx';
import {
  activeItemTo,
  itemsOf,
  locate,
  menuTarget,
  type NavArea,
  opensMenu,
  screensOf,
} from '../navigation.ts';
import { usePlatformLook } from '../platform.tsx';
import { AreaTree, MenuLink } from './menu.tsx';

/**
 * Layout "Trilho e coluna" (ADR 0128): as áreas num trilho de ícones com o nome embaixo e, ao lado,
 * uma coluna com as telas da área escolhida. Escolher uma área no trilho só troca a coluna; quem
 * leva a uma tela é a coluna. Área sem coluna (`opensMenu`: Início, em preparação, Configuração)
 * vai direto para a página.
 */
export function AreaRail({
  areas,
  selected,
  onSelect,
  onSearch,
  linkAreas = false,
}: {
  areas: NavArea[];
  selected: string | undefined;
  onSelect: (areaId: string) => void;
  onSearch: () => void;
  /** Sem coluna ao lado (Mestre e detalhe): cada área leva direto à primeira tela dela. */
  linkAreas?: boolean;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  const item = (area: NavArea) => {
    const Icon = area.icon;
    const label = t(area.railKey ?? area.key);
    const active = area.id === (selected ?? current?.id);
    const className = `flex w-16 shrink-0 flex-col items-center gap-1 rounded-md px-1 py-2 text-center text-[11px] leading-tight ${
      active ? 'ui-nav-active font-medium' : 'text-rail-text hover:bg-paper hover:text-ink'
    }`;
    const body = (
      <>
        <Icon size={20} strokeWidth={1.75} aria-hidden />
        <span className="line-clamp-2 w-full hyphens-auto wrap-anywhere">{label}</span>
      </>
    );
    if (linkAreas || !opensMenu(area))
      return (
        <Link
          key={area.id}
          to={itemsOf(area)[0]!.to}
          onClick={() => onSelect(area.id)}
          title={t(area.key)}
          aria-current={area.id === current?.id ? 'true' : undefined}
          className={className}
        >
          {body}
        </Link>
      );
    return (
      <button
        key={area.id}
        type="button"
        onClick={() => onSelect(area.id)}
        title={t(area.key)}
        aria-pressed={active}
        aria-current={area.id === current?.id ? 'true' : undefined}
        className={className}
      >
        {body}
      </button>
    );
  };
  const footLink = (to: string, label: string, Icon: LucideIcon) => (
    <Link
      to={to}
      aria-label={label}
      title={label}
      aria-current={pathname === to ? 'page' : undefined}
      className={`flex size-10 items-center justify-center rounded-md ${
        pathname === to ? 'ui-nav-active' : 'text-rail-text hover:bg-paper hover:text-ink'
      }`}
    >
      <Icon size={20} strokeWidth={1.75} aria-hidden />
    </Link>
  );
  return (
    <nav
      aria-label={t('nav.label')}
      className="ui-sidebar flex h-full min-h-0 w-20 flex-col items-center gap-1 border-r border-rule py-3"
    >
      <Link to="/" aria-label={t('nav.homeBrand')} className="mb-1 flex shrink-0 py-1">
        <img src={look.symbolUrl} alt="" className="h-[26px] w-auto max-w-10 object-contain" />
      </Link>
      <button
        type="button"
        onClick={onSearch}
        aria-label={t('nav.search')}
        title={`${t('nav.search')} (Ctrl K)`}
        className="flex size-10 shrink-0 items-center justify-center rounded-md text-rail-text hover:bg-paper hover:text-ink"
      >
        <Search size={18} strokeWidth={1.75} aria-hidden />
      </button>
      <NavScroll className="flex min-h-0 flex-1 flex-col items-center gap-1 py-1">
        {areas.filter((a) => !a.bottom).map(item)}
      </NavScroll>
      <div className="flex shrink-0 flex-col items-center gap-1 border-t border-rule pt-2">
        {areas.filter((a) => a.bottom).map(item)}
        {footLink('/account', t('nav.account'), UserRound)}
        {footLink('/help', t('nav.help'), CircleHelp)}
        <button
          type="button"
          onClick={() => void logout()}
          aria-label={t('auth.logout')}
          title={t('auth.logout')}
          className="flex size-10 items-center justify-center rounded-md text-rail-text hover:bg-paper hover:text-ink"
        >
          <LogOut size={20} strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </nav>
  );
}

/** A coluna da área escolhida no trilho: o nome da área e as telas dela, como na lateral. */
export function AreaColumn({ area }: { area: NavArea }) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const target = menuTarget([area], pathname);
  const [openGroup, setOpenGroup] = useState(target.groupKey);
  const [synced, setSynced] = useState(`${area.id}${pathname}`);
  if (synced !== `${area.id}${pathname}`) {
    setSynced(`${area.id}${pathname}`);
    setOpenGroup(target.groupKey);
  }
  const activeTo = activeItemTo([area], pathname);
  return (
    <aside
      aria-label={t(area.key)}
      className="flex h-full min-h-0 w-60 shrink-0 flex-col gap-2 border-r border-rule bg-sheet px-3 py-4"
    >
      <p className="px-2.5 text-sm font-semibold text-ink">{t(area.key)}</p>
      <NavScroll className="-mx-1 flex min-h-0 flex-1 flex-col gap-0.5 px-1">
        {area.id === 'home' || screensOf(area).length === 0 ? (
          itemsOf(area).map((item) => (
            <MenuLink
              key={item.to}
              to={item.to}
              label={t(item.key)}
              icon={item.icon}
              active={item.to === activeTo}
            />
          ))
        ) : (
          <AreaTree
            area={area}
            idPrefix="column"
            openGroup={openGroup}
            onToggleGroup={(key) => setOpenGroup((g) => (g === key ? undefined : key))}
          />
        )}
      </NavScroll>
    </aside>
  );
}
